import { API_BASE_URL } from "../../constant/constant";
import React, { useEffect, useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "moment/locale/nl"; // Import Dutch locale
import "react-big-calendar/lib/css/react-big-calendar.css";
import Holidays from "date-holidays";
import { Container, Header, Heading } from "../Summarize/Style";
import BackIcon from "../../assets/back-icon.svg";
import PlusIcon from "../../assets/plus-icon-white.svg";
import { InfoCircleOutlined } from "@ant-design/icons";
import {
  TimezoneContainer,
  TimezoneSelect,
  Option,
  SubTitle,
  CreateTaskBtn,

} from "./style";
import { Tooltip } from "antd";
import { RRule } from "rrule";
import CustomEvent from "./CustomEvent";
import TaskModal from "../modals/TaskModal";
import AddTaskModal from "../modals/AddTaskModal";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "../../context/SnackBarContext";

const localizer = momentLocalizer(moment);

const AvatarCalendar = () => {
  const { t, i18n } = useTranslation();
  const { isSidebarOpen } = useOutletContext();
  const [selectedTimezone, setSelectedTimezone] = useState("NL");
  const [timezones, setTimezones] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [events, setEvents] = useState([]);
  const [event, setEvent] = useState(null);
  const [isTaskModelOpen, setIsTaskModelOpen] = useState(false);
  const [isAddTaskOpen, setIsAddTaskOpen] = useState(false);
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();

  const getRRuleWeekday = (day) => {
    const weekdayMap = {
      MO: RRule.MO,
      TU: RRule.TU,
      WE: RRule.WE,
      TH: RRule.TH,
      FR: RRule.FR,
      SA: RRule.SA,
      SU: RRule.SU,
    };
    return weekdayMap[day];
  };

  const generateRecurringEvents = (task) => {
    const startYear = new Date().getFullYear();
    const endYear = startYear + 20;
    const recurringEvents = [];

    if (!task.isRecurring) {
      return [
        {
          id: task._id,
          title: task.taskTitle,
          description: task.taskDescription,
          start: new Date(task.start),
          end: new Date(task.end),
          isRecurring: task.isRecurring,
          recurrenceType: task.recurrenceType,
          byweekday: task.byweekday,
          byweekdayofmonth: task.byweekdayofmonth,
          bydateofmonth: task.bydateofmonth,
          cronExpression: task.cronExpression,
          isWorkflowTask: task.isWorkflowTask,
          scheduleId: task.scheduleId,
          userId: task.userId,
        },
      ];
    }

    let ruleOptions = {
      dtstart: new Date(task.start),
      until: moment().year(endYear).endOf("year").toDate(),
    };

    switch (task.recurrenceType?.toLowerCase()) {
      case "daily":
        ruleOptions.freq = RRule.DAILY;
        break;

      case "weekly":
        ruleOptions.freq = RRule.WEEKLY;
        ruleOptions.byweekday = task.byweekday.map(getRRuleWeekday);
        break;

      case "monthly":
        ruleOptions.freq = RRule.MONTHLY;
        if (task.byweekdayofmonth?.day) {
          const weekday = getRRuleWeekday(task.byweekdayofmonth.day);
          ruleOptions.byweekday = [
            weekday.nth(task.byweekdayofmonth.occurrence),
          ];
        } else if (task.bydateofmonth?.length) {
          ruleOptions.bymonthday = task.bydateofmonth;
        }
        break;

      default:
        break;
    }

    const rule = new RRule(ruleOptions);

    rule.all().forEach((date) => {
      recurringEvents.push({
        id: task._id,
        title: task.taskTitle,
        description: task.taskDescription,
        start: moment(date).toDate(),
        end: moment(date)
          .add(moment(task.end).diff(moment(task.start), "minutes"), "minutes")
          .toDate(),
        isRecurring: true,
        recurrenceType: task.recurrenceType,
        isWorkflowTask: task.isWorkflowTask,
      });
    });

    return recurringEvents;
  };

  const fetchEvents = async () => {
    try {
      const userId = localStorage.getItem("id");
      const response = await fetch(
        `${API_BASE_URL}/api/task?user_id=${userId}`,
        {
          headers: {
            "Content-Type": "application/json",
            "x-auth-token": `${localStorage.getItem("token")}`,
          },
        }
      );
      const data = await response.json();

      if (data.responseCode === 429) {
        showSnackbar(data.message, "warning");
        return;
      }

      if (data.responseCode === 200) {
        const allEvents = data.tasks.flatMap(generateRecurringEvents);
        setEvents(allEvents);
      }
    } catch (error) {
      console.error("Error fetching events:", error);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [selectedTimezone]);

  useEffect(() => {
    moment.locale(i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    const hd = new Holidays();
    const [country, state] = selectedTimezone.split("/");
    hd.init(country, state);

    const currentYear = new Date().getFullYear();
    const fetchedHolidays = hd.getHolidays(currentYear).map((holiday) => ({
      id: holiday.date,
      title: holiday.name,
      start: new Date(holiday.start),
      end: new Date(holiday.end),
      allDay: true,
      isHoliday: true,
    }));

    setHolidays(fetchedHolidays);
  }, [selectedTimezone]);

  useEffect(() => {
    const hd = new Holidays();
    const zones = [];

    const countries = hd.getCountries();
    Object.keys(countries).forEach((countryCode) => {
      const regions = hd.getStates(countryCode);
      if (regions) {
        Object.keys(regions).forEach((regionCode) => {
          zones.push({
            label: `${countries[countryCode]} (${regions[regionCode]})`,
            value: `${countryCode}/${regionCode}`,
          });
        });
      } else {
        zones.push({
          label: countries[countryCode],
          value: countryCode,
        });
      }
    });

    setTimezones(zones);
  }, []);

  const combinedEvents = [...events, ...holidays];

  return (
    <Container isOpen={isSidebarOpen} style={{ marginRight: "1.4rem" }}>
      <Header style={{ justifyContent: "space-between" }}>
        <div
          onClick={() => navigate("/")}
          style={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          <img src={BackIcon} alt="back" />

          <Heading style={{ marginBottom: 0 }}>
            {t("calendar.title")}
          </Heading>

          <Tooltip
            title={t("calendar.tooltip")}
            placement="top"
          >
            <InfoCircleOutlined
              style={{
                fontSize: 14,
                color: "#8c8c8c",
                cursor: "pointer",
              }}
              onClick={(e) => e.stopPropagation()} // prevent navigation click
            />
          </Tooltip>
        </div>


        <TimezoneContainer>
          <SubTitle>{t("calendar.selectTimezone")}</SubTitle>
          <TimezoneSelect
            value={selectedTimezone}
            onChange={(e) => setSelectedTimezone(e.target.value)}
          >
            <Option disabled>{t("calendar.selectTimezone")}</Option>
            {timezones.map((zone) => (
              <Option key={zone.value} value={zone.value}>
                {zone.label}
              </Option>
            ))}
          </TimezoneSelect>
        </TimezoneContainer>

        <CreateTaskBtn onClick={() => setIsAddTaskOpen(true)}>
          <img src={PlusIcon} alt="plus" />
          <span>{t("calendar.createTask")}</span>
        </CreateTaskBtn>
      </Header>

      <Calendar
        localizer={localizer}
        events={combinedEvents}
        startAccessor="start"
        endAccessor="end"
        culture={i18n.language}
        views={["month", "week", "day", "agenda"]}
        defaultView="week"
        onSelectEvent={(e) => {
          setEvent(e);
          setIsTaskModelOpen(true);
        }}
        components={{ event: CustomEvent }}
        style={{ height: "92vh", padding: "1rem" }}
        messages={{
          today: t("calendar.today"),
          previous: t("calendar.back"),
          next: t("calendar.next"),
          month: t("calendar.month"),
          week: t("calendar.week"),
          day: t("calendar.day"),
          agenda: t("calendar.agenda"),
        }}
        formats={{
          timeGutterFormat: (date, culture, localizer) =>
            localizer.format(date, "HH:mm", culture),
          eventTimeRangeFormat: ({ start, end }, culture, localizer) =>
            localizer.format(start, "HH:mm", culture) +
            " - " +
            localizer.format(end, "HH:mm", culture),
          agendaTimeRangeFormat: ({ start, end }, culture, localizer) =>
            localizer.format(start, "HH:mm", culture) +
            " - " +
            localizer.format(end, "HH:mm", culture),
        }}
      />

      {isTaskModelOpen && (
        <TaskModal
          event={event}
          closeModal={() => setIsTaskModelOpen(false)}
          fetchEvents={fetchEvents}
        />
      )}

      {isAddTaskOpen && (
        <AddTaskModal
          closeAddTaskModal={() => setIsAddTaskOpen(false)}
          fetchEvents={fetchEvents}
        />
      )}
    </Container>
  );
};

export default AvatarCalendar;
